import { useCallback, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import {
    type BarDuration,
    type ChartPatternAuditArtifact,
    type ChartPatternAuditBatchSummary,
    type ChartPatternAuditQueueItem,
    type ChartPatternKey as ChartPatternKeyType,
    type ChartPatternState as ChartPatternStateType,
    ChartPatternAuditArtifactType,
    ChartPatternAuditReviewerType
} from '@einhaus/project314-types';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { showToast } from '@/components/Layout/Toast';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import {
    ChartPatternAuditFullscreenModal,
    ChartPatternAuditReviewCenterPanel,
    ChartPatternAuditReviewInspectorPanel,
    ChartPatternAuditReviewSidebar,
    ChartPatternAuditReviewTopBar,
    ChartPatternAuditShortcutHelpBanner
} from './ChartPatternAuditReviewUi';
import { type ReviewFormState, confidenceMap } from './chartPatternAuditConstants';

type ChartPatternAuditReviewProps = {
    batchId: number;
    onBack: () => void;
    onStartReview: (batchId: number) => void;
};

type ArtifactAnnotation = 'annotated' | 'raw';
type ArtifactZoom = 'focused' | 'wide';

const getArtifactType = (annotation: ArtifactAnnotation, zoom: ArtifactZoom): ChartPatternAuditArtifactType => {
    if (annotation === 'annotated' && zoom === 'focused') return ChartPatternAuditArtifactType.annotatedFocused;
    if (annotation === 'annotated' && zoom === 'wide') return ChartPatternAuditArtifactType.annotatedWide;
    if (annotation === 'raw' && zoom === 'focused') return ChartPatternAuditArtifactType.rawFocused;
    return ChartPatternAuditArtifactType.rawWide;
};

const getActiveItemId = (items: ChartPatternAuditQueueItem[], selectedItemId: number | null): number | null => {
    if (!items.length) {
        return null;
    }

    if (selectedItemId !== null && items.some((item) => item.id === selectedItemId)) {
        return selectedItemId;
    }

    return items[0].id;
};

const buildListItemsInput = (params: {
    batchId: number;
    filterPattern: ChartPatternKeyType | '';
    filterStatus: ChartPatternStateType | '';
    filterTimeframe: BarDuration | '';
    filterUnreviewed: boolean;
}) => {
    const { batchId, filterPattern, filterStatus, filterTimeframe, filterUnreviewed } = params;

    return {
        batchId,
        patternKey: filterPattern || null,
        barDuration: filterTimeframe || null,
        state: filterStatus || null,
        humanVerdict: null,
        onlyUnreviewed: filterUnreviewed
    };
};

const getProgressMetrics = (batch: ChartPatternAuditBatchSummary | null) => {
    const reviewedCount = batch?.reviewedItems ?? 0;
    const totalCount = batch?.totalItems ?? 0;

    return {
        reviewedCount,
        totalCount,
        progressPct: totalCount > 0 ? (reviewedCount / totalCount) * 100 : 0,
        isBatchComplete: totalCount > 0 && reviewedCount >= totalCount
    };
};

const getNextIncompleteBatch = (
    batchId: number,
    batches: ChartPatternAuditBatchSummary[] | undefined
): ChartPatternAuditBatchSummary | null => {
    const sortedBatches = [...(batches ?? [])];

    sortedBatches.sort((left, right) => {
        if (left.createdAt === right.createdAt) {
            return right.id - left.id;
        }

        return right.createdAt.localeCompare(left.createdAt);
    });

    return (
        sortedBatches.find((candidateBatch) => candidateBatch.id !== batchId && candidateBatch.reviewedItems < candidateBatch.totalItems) ??
        null
    );
};

type UseChartPatternAuditReviewDataArgs = {
    batchId: number;
    failedArtifactId: number | null;
    filterPattern: ChartPatternKeyType | '';
    filterStatus: ChartPatternStateType | '';
    filterTimeframe: BarDuration | '';
    filterUnreviewed: boolean;
    imageAnnotation: ArtifactAnnotation;
    imageZoom: ArtifactZoom;
    selectedItemId: number | null;
};

const useChartPatternAuditReviewData = ({
    batchId,
    failedArtifactId,
    filterPattern,
    filterStatus,
    filterTimeframe,
    filterUnreviewed,
    imageAnnotation,
    imageZoom,
    selectedItemId
}: UseChartPatternAuditReviewDataArgs) => {
    const batchQuery = useAppQuery({
        ...trpc.chartPatternAudit.listBatches.queryOptions()
    });

    const batch: ChartPatternAuditBatchSummary | null = useMemo(
        () => batchQuery.data?.batches.find((candidateBatch) => candidateBatch.id === batchId) ?? null,
        [batchId, batchQuery.data?.batches]
    );

    const itemsQuery = useAppQuery({
        ...trpc.chartPatternAudit.listItems.queryOptions(
            buildListItemsInput({ batchId, filterPattern, filterStatus, filterTimeframe, filterUnreviewed })
        )
    });

    const items = useMemo(() => itemsQuery.data?.items ?? [], [itemsQuery.data?.items]);

    const activeItemId = useMemo(() => getActiveItemId(items, selectedItemId), [items, selectedItemId]);

    const detailQuery = useAppQuery({
        ...trpc.chartPatternAudit.getItemDetail.queryOptions(activeItemId ? { auditItemId: activeItemId } : { auditItemId: 0 }),
        enabled: activeItemId !== null
    });

    const detail = activeItemId === null ? null : (detailQuery.data?.detail ?? null);

    const currentIndex = useMemo(() => items.findIndex((item) => item.id === activeItemId), [activeItemId, items]);

    const artifactByType = useMemo(() => {
        const artifacts = new Map<ChartPatternAuditArtifactType, ChartPatternAuditArtifact>();

        for (const artifact of detail?.artifacts ?? []) {
            artifacts.set(artifact.artifactType, artifact);
        }

        return artifacts;
    }, [detail?.artifacts]);

    const currentArtifact = useMemo(
        () => artifactByType.get(getArtifactType(imageAnnotation, imageZoom)) ?? null,
        [artifactByType, imageAnnotation, imageZoom]
    );

    const artifactLoadFailed = failedArtifactId === currentArtifact?.id;

    const currentOccurrenceSnapshot = useMemo(
        () => detail?.overlaps.find((snapshot) => snapshot.occurrenceId === detail.item.occurrenceId) ?? null,
        [detail]
    );

    const { isBatchComplete, progressPct, reviewedCount, totalCount } = useMemo(() => getProgressMetrics(batch), [batch]);

    const nextIncompleteBatch = useMemo(
        () => getNextIncompleteBatch(batchId, batchQuery.data?.batches),
        [batchId, batchQuery.data?.batches]
    );

    return {
        activeItemId,
        artifactLoadFailed,
        batch,
        batchQuery,
        currentArtifact,
        currentIndex,
        currentOccurrenceSnapshot,
        detail,
        detailQuery,
        isBatchComplete,
        items,
        itemsQuery,
        nextIncompleteBatch,
        progressPct,
        reviewedCount,
        totalCount
    };
};

const ChartPatternAuditReview = ({ batchId, onBack, onStartReview }: ChartPatternAuditReviewProps) => {
    const [selectedItemId, setSelectedItemId] = useState<number | null>(null);
    const [filterPattern, setFilterPattern] = useState<ChartPatternKeyType | ''>('');
    const [filterTimeframe, setFilterTimeframe] = useState<BarDuration | ''>('');
    const [filterStatus, setFilterStatus] = useState<ChartPatternStateType | ''>('');
    const [filterUnreviewed, setFilterUnreviewed] = useState(false);
    const [imageAnnotation, setImageAnnotation] = useState<ArtifactAnnotation>('annotated');
    const [imageZoom, setImageZoom] = useState<ArtifactZoom>('wide');
    const [showShortcutHelp, setShowShortcutHelp] = useState(false);
    const [imageFullscreen, setImageFullscreen] = useState(false);
    const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
    const [failedArtifactId, setFailedArtifactId] = useState<number | null>(null);

    const {
        activeItemId,
        artifactLoadFailed,
        batch,
        batchQuery,
        currentArtifact,
        currentIndex,
        currentOccurrenceSnapshot,
        detail,
        detailQuery,
        isBatchComplete,
        items,
        itemsQuery,
        nextIncompleteBatch,
        progressPct,
        reviewedCount,
        totalCount
    } = useChartPatternAuditReviewData({
        batchId,
        failedArtifactId,
        filterPattern,
        filterStatus,
        filterTimeframe,
        filterUnreviewed,
        imageAnnotation,
        imageZoom,
        selectedItemId
    });

    const saveReviewMutation = useMutation({
        ...trpc.chartPatternAudit.saveReview.mutationOptions({
            onSuccess: () => {
                void itemsQuery.refetch();
                void detailQuery.refetch();
                void batchQuery.refetch();
            }
        })
    });

    const renderArtifactsMutation = useMutation({
        ...trpc.chartPatternAudit.renderArtifacts.mutationOptions({
            onSuccess: async (data) => {
                const failCount = data.result.failedItemIds.length;
                showToast(failCount ? `Generated with ${failCount} failure(s)` : 'Charts generated', failCount ? 'warning' : 'success');
                await Promise.all([itemsQuery.refetch(), detailQuery.refetch()]);
            }
        })
    });

    const runAiReviewMutation = useMutation({
        ...trpc.chartPatternAudit.runAiReview.mutationOptions({
            onSuccess: async (data) => {
                const failedCount = data.result.failedItemIds.length;
                const reviewedCount = data.result.reviewedItemIds.length;
                const skippedCount = data.result.skippedItemIds.length;

                if (failedCount > 0) {
                    showToast(`AI review failed for ${failedCount} item(s)`, 'error');
                } else if (reviewedCount > 0) {
                    showToast('AI review saved', 'success');
                } else if (skippedCount > 0) {
                    showToast('AI review skipped', 'warning');
                } else {
                    showToast('No items were reviewed', 'warning');
                }

                await Promise.all([itemsQuery.refetch(), detailQuery.refetch(), batchQuery.refetch()]);
            }
        })
    });

    const goToItem = useCallback(
        (index: number) => {
            if (index >= 0 && index < items.length) {
                setSelectedItemId(items[index].id);
            }
        },
        [items]
    );

    const goNext = useCallback(() => {
        if (currentIndex < items.length - 1) {
            goToItem(currentIndex + 1);
        }
    }, [currentIndex, goToItem, items.length]);

    const goPrev = useCallback(() => {
        if (currentIndex > 0) {
            goToItem(currentIndex - 1);
        }
    }, [currentIndex, goToItem]);

    const submitReview = useCallback(
        async (reviewForm: ReviewFormState): Promise<boolean> => {
            if (!activeItemId || !reviewForm.verdict) {
                showToast('Choose a verdict before saving', 'error');
                return false;
            }

            try {
                await saveReviewMutation.mutateAsync({
                    auditItemId: activeItemId,
                    reviewerType: ChartPatternAuditReviewerType.human,
                    verdict: reviewForm.verdict,
                    confidence: reviewForm.confidence ? (confidenceMap[reviewForm.confidence] ?? null) : null,
                    reasonCodes: reviewForm.reasonCodes,
                    notes: reviewForm.notes.trim() === '' ? null : reviewForm.notes.trim(),
                    isFinal: true
                });

                return true;
            } catch (error) {
                showToast(error instanceof Error ? error.message : 'Failed to save review', 'error');
                return false;
            }
        },
        [activeItemId, saveReviewMutation]
    );

    const handleSaveReview = useCallback(
        async (reviewForm: ReviewFormState) => {
            await submitReview(reviewForm);
        },
        [submitReview]
    );

    const handleSaveAndNext = useCallback(
        async (reviewForm: ReviewFormState) => {
            const wasSaved = await submitReview(reviewForm);

            if (wasSaved && currentIndex < items.length - 1) {
                goToItem(currentIndex + 1);
            }
        },
        [currentIndex, goToItem, items.length, submitReview]
    );

    const handleRenderArtifacts = useCallback(() => {
        if (!detail) {
            return;
        }

        setFailedArtifactId(null);
        renderArtifactsMutation.mutate({ auditItemId: detail.item.id, overwrite: true, limit: 1 });
    }, [detail, renderArtifactsMutation]);

    const handleOpenNextAudit = useCallback(() => {
        if (!nextIncompleteBatch) {
            return;
        }

        onStartReview(nextIncompleteBatch.id);
    }, [nextIncompleteBatch, onStartReview]);

    const handleRunAiReview = useCallback(() => {
        if (!detail) {
            return;
        }

        if (detail.item.renderStatus !== 'rendered' || detail.artifacts.length === 0 || artifactLoadFailed) {
            showToast('Generate charts before running AI review', 'warning');
            return;
        }

        runAiReviewMutation.mutate({
            auditItemId: detail.item.id,
            overwrite: true
        });
    }, [artifactLoadFailed, detail, runAiReviewMutation]);

    const handleToggleShortcutHelp = useCallback(() => {
        setShowShortcutHelp((current) => !current);
    }, []);

    if (itemsQuery.isLoading) {
        return <DataLoadingSpinner text="Loading review queue…" />;
    }

    return (
        <div className="flex flex-col h-[calc(100vh-120px)]">
            <ChartPatternAuditReviewTopBar
                batch={batch}
                batchId={batchId}
                isBatchComplete={isBatchComplete}
                nextIncompleteBatch={nextIncompleteBatch}
                progressPct={progressPct}
                reviewedCount={reviewedCount}
                totalCount={totalCount}
                onBack={onBack}
                onOpenNextAudit={handleOpenNextAudit}
                onToggleShortcutHelp={handleToggleShortcutHelp}
            />

            <ChartPatternAuditShortcutHelpBanner showShortcutHelp={showShortcutHelp} />

            <div className="flex flex-1 min-h-0">
                <ChartPatternAuditReviewSidebar
                    currentIndex={currentIndex}
                    filterPattern={filterPattern}
                    filterStatus={filterStatus}
                    filterTimeframe={filterTimeframe}
                    filterUnreviewed={filterUnreviewed}
                    isCollapsed={sidebarCollapsed}
                    items={items}
                    selectedItemId={activeItemId}
                    onFilterPatternChange={setFilterPattern}
                    onFilterStatusChange={setFilterStatus}
                    onFilterTimeframeChange={setFilterTimeframe}
                    onFilterUnreviewedChange={setFilterUnreviewed}
                    onSelectItem={setSelectedItemId}
                    onToggleCollapsed={() => setSidebarCollapsed((current) => !current)}
                />

                <ChartPatternAuditReviewCenterPanel
                    artifactLoadFailed={artifactLoadFailed}
                    currentArtifact={currentArtifact}
                    detail={detail}
                    detailIsLoading={detailQuery.isLoading}
                    imageAnnotation={imageAnnotation}
                    imageZoom={imageZoom}
                    isRenderingArtifacts={renderArtifactsMutation.isPending}
                    isRunningAiReview={runAiReviewMutation.isPending}
                    onArtifactLoadError={setFailedArtifactId}
                    onOpenFullscreen={() => setImageFullscreen(true)}
                    onRenderArtifacts={handleRenderArtifacts}
                    onRunAiReview={handleRunAiReview}
                    onSetImageAnnotation={setImageAnnotation}
                    onSetImageZoom={setImageZoom}
                />

                {detail ? (
                    <ChartPatternAuditReviewInspectorPanel
                        currentIndex={currentIndex}
                        currentOccurrenceSnapshot={currentOccurrenceSnapshot}
                        detail={detail}
                        isBatchComplete={isBatchComplete}
                        isSaving={saveReviewMutation.isPending}
                        itemsLength={items.length}
                        key={detail.item.id}
                        nextIncompleteBatch={nextIncompleteBatch}
                        onGoNext={goNext}
                        onGoPrev={goPrev}
                        onOpenNextAudit={handleOpenNextAudit}
                        onSaveAndNext={handleSaveAndNext}
                        onSaveReview={handleSaveReview}
                        onToggleShortcutHelp={handleToggleShortcutHelp}
                    />
                ) : null}
            </div>

            <ChartPatternAuditFullscreenModal
                artifactLoadFailed={artifactLoadFailed}
                currentArtifact={currentArtifact}
                imageFullscreen={imageFullscreen}
                onClose={() => setImageFullscreen(false)}
            />
        </div>
    );
};

export default ChartPatternAuditReview;
